
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface PrivatePageSkeletonProps {
  variant?: "dashboard" | "table" | "form" | "analytics" | "calendar" | "meeting-links";
  className?: string;
}

export function PrivatePageSkeleton({ variant = "dashboard", className }: PrivatePageSkeletonProps) {
  // Sidebar skeleton component - matches the actual sidebar layout
  const SidebarSkeleton = () => (
    <div className="w-56 bg-zinc-50 border-r border-border shadow-md relative">
      <div className="p-4 space-y-4">
        {/* Logo area */}
        <Skeleton className="h-6 w-32" />
        
        {/* Main navigation button */}
        <Skeleton className="h-10 w-full rounded-md" />
        
        {/* Navigation menu items */}
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center space-x-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
        
        {/* Energy forecast section */}
        <div className="mt-8 space-y-3">
          <Skeleton className="h-4 w-24" />
          <div className="bg-white rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-4" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-16 w-full" />
            <div className="space-y-2">
              <Skeleton className="h-3 w-20" />
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <Skeleton className="h-3 w-3" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Header skeleton component - matches the actual header
  const HeaderSkeleton = () => (
    <header className="fixed top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center px-4 justify-between bg-white">
        <div className="flex items-center gap-1">
          <Skeleton className="h-6 w-6" />
          <Skeleton className="h-4 w-px mx-2" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-8 w-8 rounded-full" />
      </div>
    </header>
  );

  if (variant === "table") {
    return (
      <div className={cn("min-h-screen flex w-full", className)}>
        <SidebarSkeleton />
        <div className="flex-1 bg-white">
          <HeaderSkeleton />
          <main className="pt-16 p-8 space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-8 w-24" />
              </div>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (variant === "form") {
    return (
      <div className={cn("min-h-screen flex w-full", className)}>
        <SidebarSkeleton />
        <div className="flex-1 bg-white">
          <HeaderSkeleton />
          <main className="pt-16 p-8 max-w-2xl">
            <div className="space-y-6">
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <div className="space-y-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
                <div className="space-y-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-20 w-full" />
                </div>
                <div className="flex gap-4">
                  <Skeleton className="h-10 w-24" />
                  <Skeleton className="h-10 w-20" />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (variant === "analytics") {
    return (
      <div className={cn("min-h-screen flex w-full", className)}>
        <SidebarSkeleton />
        <div className="flex-1 bg-white">
          <HeaderSkeleton />
          <main className="pt-16 p-8 space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-20 w-full rounded-lg" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
            <Skeleton className="h-64 w-full rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-48 w-full rounded-lg" />
              <Skeleton className="h-48 w-full rounded-lg" />
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (variant === "calendar") {
    return (
      <div className={cn("min-h-screen flex w-full", className)}>
        <SidebarSkeleton />
        <div className="flex-1 bg-white">
          <HeaderSkeleton />
          <main className="pt-16 p-8 space-y-6">
            <div className="space-y-2">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex justify-between items-center">
              <Skeleton className="h-8 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
            <Skeleton className="h-96 w-full rounded-lg" />
          </main>
        </div>
      </div>
    );
  }

  if (variant === "meeting-links") {
    return (
      <div className={cn("min-h-screen flex w-full", className)}>
        <SidebarSkeleton />
        <div className="flex-1 bg-white">
          <HeaderSkeleton />
          <main className="pt-16 p-8 space-y-6">
            <div className="flex justify-between items-center">
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-10 w-40" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="border rounded-lg overflow-hidden">
                  {/* Card Header */}
                  <div className="p-6 pb-2 pt-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Skeleton className="h-5 w-32" />
                          <Skeleton className="h-4 w-12" />
                        </div>
                        <Skeleton className="h-4 w-full mb-1" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                      <Skeleton className="h-8 w-8 rounded" />
                    </div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="px-6 pb-0 pt-0">
                    {/* Meeting URL input */}
                    <div className="flex items-center gap-2 bg-muted/40 rounded-lg px-2 py-2 mb-4 border bg-white">
                      <Skeleton className="h-4 flex-1" />
                      <Skeleton className="h-8 w-8 rounded" />
                    </div>
                    
                    {/* Energy badge and location icon */}
                    <div className="flex items-center justify-between mb-4">
                      <Skeleton className="h-6 w-16 rounded-full" />
                      <Skeleton className="h-4 w-4 rounded" />
                    </div>
                  </div>
                  
                  {/* Card Footer */}
                  <div className="flex-col border-t bg-muted/20 py-2 px-4">
                    <div className="w-full flex items-center justify-between">
                      {/* Stats */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Skeleton className="h-4 w-4 rounded" />
                          <Skeleton className="h-4 w-8" />
                        </div>
                        <div className="flex items-center gap-1">
                          <Skeleton className="h-4 w-4 rounded" />
                          <Skeleton className="h-4 w-4" />
                        </div>
                        <div className="flex items-center gap-1">
                          <Skeleton className="h-4 w-4 rounded" />
                          <Skeleton className="h-4 w-8" />
                        </div>
                      </div>
                      
                      {/* Open button */}
                      <Skeleton className="h-8 w-16 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Default dashboard variant - matches the meeting links layout
  return (
    <div className={cn("min-h-screen flex w-full", className)}>
      <SidebarSkeleton />
      <div className="flex-1 bg-white">
        <HeaderSkeleton />
        <main className="pt-16 p-8 space-y-6">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border rounded-lg p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-6 w-6" />
                </div>
                <Skeleton className="h-4 w-full" />
                <div className="flex items-center space-x-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div className="flex space-x-4">
                    <div className="flex items-center space-x-1">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                    <div className="flex items-center space-x-1">
                      <Skeleton className="h-4 w-4" />
                      <Skeleton className="h-4 w-8" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
